import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API } from '../config/api';
import { api } from '../utils/api';

// Constants
const CACHE_TTL_MS = 30000; // 30 seconds cache

const useEducationStore = create(
  persist(
    (set, get) => ({
      // Initialize all state with empty arrays - data comes from API
      allUsers: [],
      programs: [],
      studentPrograms: [],
      presentations: [],
      slideTemplates: [],
      studentNotes: [],
      scheduledLessons: [],
      loadingPresentations: false,
      presentationError: null,
      presentationsFetchedAt: null, // Track when presentations were last fetched
      presentationsFetchPromise: null, // Prevent duplicate concurrent requests
      presentationsPagination: { limit: 50, offset: 0, total: 0, hasMore: false },
      loadingMorePresentations: false,
      // Per-id dedup for full-slide fetches so the CameraPreview prefetch and the
      // classroom's on-enter fetch don't duplicate work.
      fullPresentationFetches: {}, // { [id]: Promise }
      fullPresentationFetchedIds: [], // ids we've already fully fetched this session
      activeChat: null,

      // Homework state
      homework: [],
      studentHomework: [],
      homeworkFetchedAt: null, // Track when homework was last fetched
      homeworkFetchPromise: null, // Prevent duplicate concurrent requests
      homeworkByIdCache: {}, // Cache for individual homework by ID

      // Module tests state
      moduleTests: [],
      loadingModuleTests: false,
      moduleTestsError: null,
      moduleTestsFetchedAt: null,
      moduleTestsFetchPromise: null,
      moduleTestsByIdCache: {},
      studentModuleTests: [],          // attempts for current student
      moduleTestAttemptsByIdCache: {}, // detailed attempt data by id

      // Programs loading state
      loadingPrograms: false,
      programsError: null,
      programsFetchPromise: null, // Prevent duplicate concurrent requests
      programsFetchedAt: null,

      // Teacher management state
      teachers: [],
      loadingTeachers: false,
      teacherError: null,

      // Student management state
      students: [],
      loadingStudents: false,
      studentError: null,

      // Admin management state
      admins: [],
      loadingAdmins: false,
      adminError: null,

      // Invitation management state
      teacherInvitations: [],
      loadingTeacherInvitations: false,
      adminInvitations: [],
      loadingAdminInvitations: false,

      // Users loading state for chat search
      loadingUsers: false,
      usersError: null,

      setAllUsers: (usersOrUpdater) => set((state) => ({
        allUsers: typeof usersOrUpdater === 'function'
          ? usersOrUpdater(state.allUsers || [])
          : usersOrUpdater
      })),
      setPrograms: (programs) => set({ programs }),
      setStudentPrograms: (sp) => set({ studentPrograms: sp }),
      setPresentations: (pOrUpdater) => set((state) => ({
        presentations: typeof pOrUpdater === 'function'
          ? pOrUpdater(state.presentations)
          : pOrUpdater
      })),
      setSlideTemplates: (t) => set({ slideTemplates: t }),
      setStudentNotes: (notesOrUpdater) => set((state) => ({
        studentNotes: typeof notesOrUpdater === 'function'
          ? notesOrUpdater(Array.isArray(state.studentNotes) ? state.studentNotes : [])
          : notesOrUpdater
      })),
      setScheduledLessons: (lessons) => set({ scheduledLessons: lessons }),
      setActiveChat: (chat) => set({ activeChat: chat }),

      // Reset store to initial state (call on logout)
      resetStore: () => set({
        allUsers: [],
        programs: [],
        studentPrograms: [],
        presentations: [],
        slideTemplates: [],
        studentNotes: [],
        scheduledLessons: [],
        homework: [],
        studentHomework: [],
        homeworkFetchedAt: null,
        homeworkFetchPromise: null,
        homeworkByIdCache: {},
        teachers: [],
        students: [],
        admins: [],
        teacherInvitations: [],
        adminInvitations: [],
        activeChat: null,
      }),

      // Programs API methods - with deduplication and caching
      fetchPrograms: async (forceRefresh = false) => {
        const state = useEducationStore.getState();
        const CACHE_TTL = CACHE_TTL_MS;
        const now = Date.now();

        // Return cached data if still valid and not forcing refresh
        if (!forceRefresh && state.programsFetchedAt && (now - state.programsFetchedAt) < CACHE_TTL) {
          return state.programs;
        }

        // Return existing promise if fetch is in progress
        if (state.programsFetchPromise) {
          return state.programsFetchPromise;
        }

        const fetchPromise = (async () => {
          set({ loadingPrograms: true, programsError: null });
          try {
            const res = await fetch(`${API.PROGRAMS}`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
              // Map backend fields to frontend format with validation
              const programs = (data.programs || []).filter(p => p && typeof p === 'object').map(p => ({
                ...p,
                ageCategory: p.target_audience,
                lessonsCount: p.lessons_count,
                matchingMetadata: p.matching_metadata || {},
                modules: (p.modules || []).filter(m => m && typeof m === 'object').map(m => ({
                  id: m.id,
                  title: m.title,
                  description: m.description,
                  order: m.order_index,
                  parentModuleId: m.parent_module_id || null,
                  groupTitle: m.group_title || '',
                })),
                lessons: (p.lessons || []).filter(l => l && typeof l === 'object').map(l => ({
                  ...l,
                  presentationId: l.presentation_id,
                  homeworkId: l.homework_id,
                  moduleId: l.module_id,
                  order: l.order_index,
                })),
              }));
              set({ programs, loadingPrograms: false, programsFetchedAt: Date.now(), programsFetchPromise: null });
              return programs;
            } else {
              set({ programsError: data.error, loadingPrograms: false, programsFetchPromise: null });
              return [];
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Error fetching programs:', err);
            set({ programsError: err.message, loadingPrograms: false, programsFetchPromise: null });
            return [];
          }
        })();

        set({ programsFetchPromise: fetchPromise });
        return fetchPromise;
      },

      fetchStudentPrograms: async (studentId) => {
        try {
          const res = await fetch(`${API.PROGRAMS}/student/${studentId}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ studentPrograms: data.studentPrograms || [] });
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching student programs:', err);
        }
      },

      createProgramAPI: async (programData) => {
        try {
          const res = await fetch(`${API.PROGRAMS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              id: programData.id,
              title: programData.title,
              description: programData.description,
              level: programData.level,
              lessons_count: programData.lessons?.length || 0,
              target_audience: programData.ageCategory || programData.target_audience,
              matching_metadata: programData.matchingMetadata || programData.matching_metadata,
              next_program_id: programData.nextProgramId ?? programData.next_program_id ?? null,
            }),
          });
          const data = await res.json();
          if (res.ok) {
            const programId = data.program.id;

            // Save modules + lessons if provided. If this fails the program
            // row was already created but its structure is stale — surface the
            // real error so the admin sees why (e.g. completed-lesson lock,
            // alt-count mismatch) instead of a silent "saved" that lies.
            if (programData.lessons && programData.lessons.length > 0) {
              const lessonsRes = await fetch(`${API.PROGRAMS}/${programId}/lessons`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  modules: programData.modules || [],
                  lessons: programData.lessons,
                }),
              });
              if (lessonsRes.ok) {
                const lessonsData = await lessonsRes.json();
                data.program.lessons = lessonsData.lessons;
                data.program.modules = lessonsData.modules;
              } else {
                const errBody = await lessonsRes.json().catch(() => ({}));
                return { success: false, error: errBody.error || 'Failed to save program structure' };
              }
            }

            const program = {
              ...data.program,
              ageCategory: data.program.target_audience,
              lessonsCount: data.program.lessons_count,
              matchingMetadata: data.program.matching_metadata || {},
              modules: (data.program.modules || []).map(m => ({
                id: m.id,
                title: m.title,
                description: m.description,
                order: m.order_index,
                parentModuleId: m.parent_module_id || null,
                groupTitle: m.group_title || '',
              })),
              lessons: (data.program.lessons || []).map(l => ({
                ...l,
                presentationId: l.presentation_id,
                homeworkId: l.homework_id,
                moduleId: l.module_id,
                order: l.order_index,
              })),
            };
            set((state) => ({
              programs: [...state.programs, program],
            }));
            return { success: true, program };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateProgramAPI: async (id, programData) => {
        try {
          const res = await fetch(`${API.PROGRAMS}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              title: programData.title,
              description: programData.description,
              level: programData.level,
              lessons_count: programData.lessons?.length || 0,
              target_audience: programData.ageCategory || programData.target_audience,
              matching_metadata: programData.matchingMetadata || programData.matching_metadata,
              next_program_id: programData.nextProgramId ?? programData.next_program_id ?? null,
            }),
          });
          const data = await res.json();
          if (res.ok) {
            // Sync structure (modules + lessons) when present. The race-
            // condition wipe is prevented at the form level: ProgramEditor
            // disables Save while the program is still loading, so by the
            // time we get here programData reflects real user intent.
            // If the structure save fails (completed-lesson lock, alt-count
            // mismatch, etc), surface the error so the admin sees why — the
            // program metadata already updated but the structure didn't.
            if (programData.lessons) {
              const lessonsRes = await fetch(`${API.PROGRAMS}/${id}/lessons`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  modules: programData.modules || [],
                  lessons: programData.lessons,
                }),
              });
              if (lessonsRes.ok) {
                const lessonsData = await lessonsRes.json();
                data.program.lessons = lessonsData.lessons;
                data.program.modules = lessonsData.modules;
              } else {
                const errBody = await lessonsRes.json().catch(() => ({}));
                return { success: false, error: errBody.error || 'Failed to save program structure' };
              }
            }

            const program = {
              ...data.program,
              ageCategory: data.program.target_audience,
              lessonsCount: data.program.lessons_count,
              matchingMetadata: data.program.matching_metadata || {},
              modules: (data.program.modules || []).map(m => ({
                id: m.id,
                title: m.title,
                description: m.description,
                order: m.order_index,
                parentModuleId: m.parent_module_id || null,
                groupTitle: m.group_title || '',
              })),
              lessons: (data.program.lessons || []).map(l => ({
                ...l,
                presentationId: l.presentation_id,
                homeworkId: l.homework_id,
                moduleId: l.module_id,
                order: l.order_index,
              })),
            };
            set((state) => ({
              programs: state.programs.map(p => p.id === id ? program : p),
            }));
            return { success: true, program };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      duplicateProgramAPI: async (id) => {
        try {
          const res = await fetch(`${API.PROGRAMS}/${id}/duplicate`, {
            method: 'POST',
            credentials: 'include',
          });
          const data = await res.json();
          if (res.ok) {
            const program = {
              ...data.program,
              ageCategory: data.program.target_audience,
              lessonsCount: data.program.lessons_count,
              matchingMetadata: data.program.matching_metadata || {},
              modules: (data.program.modules || []).map(m => ({
                id: m.id,
                title: m.title,
                description: m.description,
                order: m.order_index,
                parentModuleId: m.parent_module_id || null,
                groupTitle: m.group_title || '',
              })),
              lessons: (data.program.lessons || []).map(l => ({
                ...l,
                presentationId: l.presentation_id,
                homeworkId: l.homework_id,
                moduleId: l.module_id,
                order: l.order_index,
              })),
            };
            set((state) => ({ programs: [...state.programs, program] }));
            return { success: true, program };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      deleteProgramAPI: async (id) => {
        try {
          const res = await fetch(`${API.PROGRAMS}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              programs: state.programs.filter(p => p.id !== id),
            }));
            return { success: true };
          }
          const data = await res.json();
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      enrollInProgram: async (enrollmentData) => {
        try {
          const res = await fetch(`${API.PROGRAMS}/enroll`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(enrollmentData),
          });
          const data = await res.json();
          if (res.ok) {
            set((state) => ({
              studentPrograms: [...state.studentPrograms, data.studentProgram],
            }));
            return { success: true, studentProgram: data.studentProgram };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      // Homework loading state
      loadingHomework: false,
      homeworkError: null,

      // Homework API methods - with deduplication and caching
      fetchHomework: async (forceRefresh = false) => {
        const state = useEducationStore.getState();
        const now = Date.now();

        // Return cached data if still valid and not forcing refresh
        if (!forceRefresh && state.homeworkFetchedAt && (now - state.homeworkFetchedAt) < CACHE_TTL_MS) {
          return state.homework;
        }

        // Return existing promise if fetch is in progress
        if (state.homeworkFetchPromise) {
          return state.homeworkFetchPromise;
        }

        const fetchPromise = (async () => {
          set({ loadingHomework: true, homeworkError: null });
          try {
            const res = await fetch(`${API.HOMEWORK}`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
              const homework = data.homework || [];
              set({
                homework,
                loadingHomework: false,
                homeworkFetchedAt: Date.now(),
                homeworkFetchPromise: null
              });
              return homework;
            } else {
              set({ homeworkError: data.error, loadingHomework: false, homeworkFetchPromise: null });
              return [];
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Error fetching homework:', err);
            set({ homeworkError: err.message, loadingHomework: false, homeworkFetchPromise: null });
            return [];
          }
        })();

        set({ homeworkFetchPromise: fetchPromise });
        return fetchPromise;
      },

      // Fetch single homework by ID (includes full exercises data)
      // Uses cache to avoid redundant API calls
      fetchHomeworkById: async (id, forceRefresh = false) => {
        const state = useEducationStore.getState();
        const now = Date.now();

        // Check if we have this homework cached with full data (exercises + vocabulary)
        if (!forceRefresh) {
          const cachedEntry = state.homeworkByIdCache[id];
          if (cachedEntry && (now - cachedEntry.fetchedAt) < CACHE_TTL_MS) {
            return cachedEntry.data;
          }
        }

        try {
          const res = await fetch(`${API.HOMEWORK}/${id}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok && data.homework) {
            // Update cache and homework array
            set((state) => {
              const existingIndex = state.homework.findIndex(h => h.id === id);
              const updatedHomework = existingIndex >= 0
                ? state.homework.map((h, i) => i === existingIndex ? data.homework : h)
                : [...state.homework, data.homework];

              return {
                homework: updatedHomework,
                homeworkByIdCache: {
                  ...state.homeworkByIdCache,
                  [id]: { data: data.homework, fetchedAt: Date.now() }
                }
              };
            });
            return data.homework;
          }
          return null;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching homework by ID:', err);
          return null;
        }
      },

      // Optimized: Fetch homework and assignment in a single request
      // Used by StudentHomeworkViewer for faster loading
      fetchHomeworkWithAssignment: async (homeworkId, assignmentId) => {
        const state = useEducationStore.getState();
        const now = Date.now();

        // Check cache first
        const cachedEntry = state.homeworkByIdCache[homeworkId];
        if (cachedEntry && (now - cachedEntry.fetchedAt) < CACHE_TTL_MS) {
          // If we have cached homework, only fetch assignment if needed
          const existingAssignment = state.studentHomework.find(sh => sh.id === assignmentId);
          if (!assignmentId || existingAssignment) {
            // Ensure state.homework holds the full (cached) version — fetchHomework
            // (list) may have overwritten this slot with a lightweight row that has
            // no `vocabulary`/`exercises` fields, which breaks viewers/previews.
            const existing = state.homework.find(h => h.id === homeworkId);
            if (existing !== cachedEntry.data) {
              set((s) => {
                const idx = s.homework.findIndex(h => h.id === homeworkId);
                const updated = idx >= 0
                  ? s.homework.map((h, i) => i === idx ? cachedEntry.data : h)
                  : [...s.homework, cachedEntry.data];
                return { homework: updated };
              });
            }
            return { homework: cachedEntry.data, assignment: existingAssignment || null };
          }
        }

        try {
          const url = assignmentId
            ? `${API.HOMEWORK}/combined/${homeworkId}?assignmentId=${assignmentId}`
            : `${API.HOMEWORK}/combined/${homeworkId}`;
          const res = await fetch(url, { credentials: 'include' });
          const data = await res.json();

          if (res.ok && data.homework) {
            // Update homework cache and array
            set((state) => {
              const existingIndex = state.homework.findIndex(h => h.id === homeworkId);
              const updatedHomework = existingIndex >= 0
                ? state.homework.map((h, i) => i === existingIndex ? data.homework : h)
                : [...state.homework, data.homework];

              // Also update studentHomework if assignment was returned
              let updatedStudentHomework = state.studentHomework;
              if (data.assignment) {
                const assignmentIndex = state.studentHomework.findIndex(sh => sh.id === assignmentId);
                if (assignmentIndex >= 0) {
                  updatedStudentHomework = state.studentHomework.map((sh, i) =>
                    i === assignmentIndex ? data.assignment : sh
                  );
                } else {
                  updatedStudentHomework = [...state.studentHomework, data.assignment];
                }
              }

              return {
                homework: updatedHomework,
                studentHomework: updatedStudentHomework,
                homeworkByIdCache: {
                  ...state.homeworkByIdCache,
                  [homeworkId]: { data: data.homework, fetchedAt: Date.now() }
                }
              };
            });
            return { homework: data.homework, assignment: data.assignment || null };
          }
          return { homework: null, assignment: null };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching homework with assignment:', err);
          return { homework: null, assignment: null };
        }
      },

      createHomeworkAPI: async (homeworkData) => {
        try {
          const res = await fetch(`${API.HOMEWORK}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(homeworkData),
          });
          const data = await res.json();
          if (res.ok) {
            set((state) => ({
              homework: [...state.homework, data.homework],
              homeworkByIdCache: {
                ...state.homeworkByIdCache,
                [data.homework.id]: { data: data.homework, fetchedAt: Date.now() }
              }
            }));
            return { success: true, homework: data.homework };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateHomeworkAPI: async (id, updates) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok) {
            set((state) => ({
              homework: state.homework.map(hw => hw.id === id ? data.homework : hw),
              homeworkByIdCache: {
                ...state.homeworkByIdCache,
                [id]: { data: data.homework, fetchedAt: Date.now() }
              }
            }));
            return { success: true, homework: data.homework };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      deleteHomeworkAPI: async (id) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => {
              const { [id]: removed, ...restCache } = state.homeworkByIdCache;
              return {
                homework: state.homework.filter(hw => hw.id !== id),
                homeworkByIdCache: restCache
              };
            });
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting homework:', err);
          return false;
        }
      },

      // ========== Module Tests API ==========

      fetchModuleTests: async (forceRefresh = false) => {
        const state = useEducationStore.getState();
        const now = Date.now();

        if (!forceRefresh && state.moduleTestsFetchedAt && (now - state.moduleTestsFetchedAt) < CACHE_TTL_MS) {
          return state.moduleTests;
        }
        if (state.moduleTestsFetchPromise) {
          return state.moduleTestsFetchPromise;
        }

        const fetchPromise = (async () => {
          set({ loadingModuleTests: true, moduleTestsError: null });
          try {
            const res = await fetch(`${API.MODULE_TESTS}`, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
              const moduleTests = data.moduleTests || [];
              set({
                moduleTests,
                loadingModuleTests: false,
                moduleTestsFetchedAt: Date.now(),
                moduleTestsFetchPromise: null,
              });
              return moduleTests;
            }
            set({ moduleTestsError: data.error, loadingModuleTests: false, moduleTestsFetchPromise: null });
            return [];
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Error fetching module tests:', err);
            set({ moduleTestsError: err.message, loadingModuleTests: false, moduleTestsFetchPromise: null });
            return [];
          }
        })();

        set({ moduleTestsFetchPromise: fetchPromise });
        return fetchPromise;
      },

      fetchModuleTestById: async (id, forceRefresh = false) => {
        const state = useEducationStore.getState();
        const now = Date.now();

        if (!forceRefresh) {
          const cached = state.moduleTestsByIdCache[id];
          if (cached && (now - cached.fetchedAt) < CACHE_TTL_MS) {
            return cached.data;
          }
          const existing = state.moduleTests.find(mt => mt.id === id);
          if (existing && existing.exercises) {
            return existing;
          }
        }

        try {
          const res = await fetch(`${API.MODULE_TESTS}/${id}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok && data.moduleTest) {
            set((s) => {
              const idx = s.moduleTests.findIndex(mt => mt.id === id);
              const updated = idx >= 0
                ? s.moduleTests.map((mt, i) => i === idx ? data.moduleTest : mt)
                : [...s.moduleTests, data.moduleTest];
              return {
                moduleTests: updated,
                moduleTestsByIdCache: {
                  ...s.moduleTestsByIdCache,
                  [id]: { data: data.moduleTest, fetchedAt: Date.now() },
                },
              };
            });
            return data.moduleTest;
          }
          return null;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching module test by ID:', err);
          return null;
        }
      },

      createModuleTestAPI: async (payload) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (res.ok) {
            set((s) => ({
              moduleTests: [...s.moduleTests, data.moduleTest],
              moduleTestsByIdCache: {
                ...s.moduleTestsByIdCache,
                [data.moduleTest.id]: { data: data.moduleTest, fetchedAt: Date.now() },
              },
            }));
            return { success: true, moduleTest: data.moduleTest };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateModuleTestAPI: async (id, updates) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok) {
            set((s) => ({
              moduleTests: s.moduleTests.map(mt => mt.id === id ? data.moduleTest : mt),
              moduleTestsByIdCache: {
                ...s.moduleTestsByIdCache,
                [id]: { data: data.moduleTest, fetchedAt: Date.now() },
              },
            }));
            return { success: true, moduleTest: data.moduleTest };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      deleteModuleTestAPI: async (id) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set((s) => {
              const { [id]: removed, ...restCache } = s.moduleTestsByIdCache;
              return {
                moduleTests: s.moduleTests.filter(mt => mt.id !== id),
                moduleTestsByIdCache: restCache,
              };
            });
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting module test:', err);
          return false;
        }
      },

      // ========== Module Test Attempts (student-side) ==========

      fetchStudentModuleTests: async (studentId) => {
        if (!studentId) return [];
        try {
          const res = await fetch(`${API.MODULE_TESTS}/student/${studentId}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            const attempts = data.attempts || [];
            set({ studentModuleTests: attempts });
            return attempts;
          }
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching student module tests:', err);
          return [];
        }
      },

      startModuleTestAttempt: async (moduleTestId, studentId) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ module_test_id: moduleTestId, student_id: studentId }),
          });
          const data = await res.json();
          if (res.ok && data.attempt) {
            set((s) => {
              const others = s.studentModuleTests.filter(a => a.id !== data.attempt.id);
              return { studentModuleTests: [data.attempt, ...others] };
            });
            return { success: true, attempt: data.attempt };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      fetchModuleTestAttemptById: async (attemptId) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}/attempt/${attemptId}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok && data.attempt) {
            set((s) => ({
              moduleTestAttemptsByIdCache: {
                ...s.moduleTestAttemptsByIdCache,
                [attemptId]: data.attempt,
              },
            }));
            return data.attempt;
          }
          return null;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching attempt:', err);
          return null;
        }
      },

      updateModuleTestAttemptAPI: async (attemptId, updates) => {
        try {
          const res = await fetch(`${API.MODULE_TESTS}/attempt/${attemptId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok && data.attempt) {
            set((s) => ({
              studentModuleTests: s.studentModuleTests.map(a => a.id === attemptId ? data.attempt : a),
              moduleTestAttemptsByIdCache: {
                ...s.moduleTestAttemptsByIdCache,
                [attemptId]: data.attempt,
              },
            }));
            return { success: true, attempt: data.attempt };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      // Helper to transform student homework data to camelCase
      _transformStudentHomework: (shList) => {
        return (shList || []).map(sh => ({
          ...sh,
          studentId: sh.student_id || sh.studentId,
          homeworkId: sh.homework_id || sh.homeworkId,
          lessonScheduleId: sh.lesson_schedule_id || sh.lessonScheduleId,
          dueDate: sh.due_date || sh.dueDate,
          maxScore: sh.max_score || sh.maxScore,
          completedAt: sh.completed_at || sh.completedAt,
          submittedLate: sh.submitted_late || sh.submittedLate,
          createdAt: sh.created_at || sh.createdAt,
          teacherFeedback: sh.teacher_feedback || sh.teacherFeedback,
          homeworkTitle: sh.homework_title || sh.homeworkTitle,
          homeworkDescription: sh.homework_description || sh.homeworkDescription,
          exercisesCount: sh.exercises_count || sh.exercisesCount,
        }));
      },

      // Student homework API methods
      fetchStudentHomework: async (studentId) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/student/${studentId}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            const transformed = get()._transformStudentHomework(data.studentHomework);
            set({ studentHomework: transformed });
            return transformed;
          }
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching student homework:', err);
          return [];
        }
      },

      fetchAllStudentHomework: async () => {
        try {
          const res = await fetch(`${API.HOMEWORK}/student-assignments/all`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            const transformed = get()._transformStudentHomework(data.studentHomework);
            set({ studentHomework: transformed });
            return transformed;
          }
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching all student homework:', err);
          return [];
        }
      },

      fetchTeacherStudentHomework: async (teacherId) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/teacher/${teacherId}/students`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            return data.studentHomework || [];
          }
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching teacher student homework:', err);
          return [];
        }
      },

      fetchTeacherPendingHomework: async (teacherId) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/teacher/${teacherId}/pending`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            return data.pending || [];
          }
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching teacher pending homework:', err);
          return [];
        }
      },

      fetchStudentHomeworkById: async (assignmentId) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/student-assignment/${assignmentId}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            return data.studentHomework || null;
          }
          return null;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching student homework by ID:', err);
          return null;
        }
      },

      assignHomeworkAPI: async (assignmentData) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/assign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(assignmentData),
          });
          const data = await res.json();
          if (res.ok) {
            set((state) => ({
              studentHomework: [...state.studentHomework, data.studentHomework],
            }));
            return { success: true, studentHomework: data.studentHomework };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateStudentHomeworkAPI: async (id, updates) => {
        try {
          const res = await fetch(`${API.HOMEWORK}/student-assignment/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok) {
            set((state) => ({
              studentHomework: state.studentHomework.map(sh => sh.id === id ? data.studentHomework : sh),
            }));
            return { success: true, studentHomework: data.studentHomework, stars: data.stars || null };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      // Local state setters (for compatibility)
      setHomework: (homework) => set({ homework }),
      setStudentHomework: (studentHomework) => set({ studentHomework }),

      // ========== Lesson Games (gamified repetition path) ==========
      // Library model: games live in the library (`libraryGames`) and are
      // attached to lessons via bindings. `lessonGamesByLesson` caches the
      // per-owner view returned by the server for the bindings manager.
      libraryGames: [],
      libraryGamesFetchedAt: null,
      lessonGamesByLesson: {},   // { [ownerId]: [boundGame, ...] }
      repetitionPath: null,      // { sections: [...], nextLessonDate }
      loadingRepetitionPath: false,

      fetchLibraryGames: async ({ force = false } = {}) => {
        const state = get();
        if (!force && state.libraryGamesFetchedAt && Date.now() - state.libraryGamesFetchedAt < CACHE_TTL_MS) {
          return state.libraryGames;
        }
        const { data, error } = await api.get(`/api/lesson-games`);
        if (error) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching library games:', error);
          return [];
        }
        set({ libraryGames: data.games || [], libraryGamesFetchedAt: Date.now() });
        return data.games || [];
      },

      // Fetch games bound to a specific lesson (program or teacher). Callers
      // pass `isTeacher=true` when the id is a teacher_lesson_id.
      fetchLessonGames: async (lessonId, isTeacher = false) => {
        const params = isTeacher ? { teacherLessonId: lessonId } : { lessonId };
        const { data, error } = await api.get(`/api/lesson-games`, { params });
        if (error) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching lesson games:', error);
          return [];
        }
        set(state => ({
          lessonGamesByLesson: { ...state.lessonGamesByLesson, [lessonId]: data.games || [] },
        }));
        return data.games || [];
      },

      // Create a library game. If lesson_id or teacher_lesson_id is included
      // in gameData, the server also creates a binding.
      createLessonGame: async (gameData) => {
        try {
          const res = await fetch(`${API.LESSON_GAMES}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(gameData),
          });
          const data = await res.json();
          if (res.ok) {
            const game = data.game;
            set(state => ({
              libraryGames: [game, ...state.libraryGames.filter(g => g.id !== game.id)],
            }));
            return { success: true, game };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateLessonGame: async (id, updates) => {
        try {
          const res = await fetch(`${API.LESSON_GAMES}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok) {
            const game = data.game;
            set(state => ({
              libraryGames: state.libraryGames.map(g => g.id === game.id ? { ...g, ...game } : g),
              lessonGamesByLesson: Object.fromEntries(
                Object.entries(state.lessonGamesByLesson).map(([owner, list]) => [
                  owner,
                  list.map(g => g.id === game.id ? { ...g, ...game } : g),
                ])
              ),
            }));
            return { success: true, game };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      // Delete a library game entirely (cascades to all bindings).
      deleteLessonGame: async (id, ownerId = null) => {
        try {
          const res = await fetch(`${API.LESSON_GAMES}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set(state => ({
              libraryGames: state.libraryGames.filter(g => g.id !== id),
              lessonGamesByLesson: Object.fromEntries(
                Object.entries(state.lessonGamesByLesson).map(([owner, list]) => [
                  owner,
                  list.filter(g => g.id !== id),
                ])
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting lesson game:', err);
          return false;
        }
      },

      // Attach a library game to a lesson (or teacher lesson).
      bindLibraryGame: async (gameId, { lessonId, teacherLessonId }) => {
        try {
          const body = teacherLessonId ? { teacher_lesson_id: teacherLessonId } : { lesson_id: lessonId };
          const res = await fetch(`${API.LESSON_GAMES}/${gameId}/bindings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok) return { success: false, error: data.error };

          // Refresh caches lazily on next fetch.
          set(state => ({
            libraryGames: state.libraryGames.map(g => g.id === gameId
              ? { ...g, bindings: [...(g.bindings || []), { id: data.binding?.id, lesson_id: lessonId || null, teacher_lesson_id: teacherLessonId || null }] }
              : g),
            libraryGamesFetchedAt: null,
          }));
          return { success: true, binding: data.binding };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      unbindLibraryGame: async (gameId, { lessonId, teacherLessonId }) => {
        try {
          const params = new URLSearchParams(
            teacherLessonId ? { teacherLessonId } : { lessonId }
          );
          const res = await fetch(`${API.LESSON_GAMES}/${gameId}/bindings?${params}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            return { success: false, error: data.error };
          }
          const ownerId = teacherLessonId || lessonId;
          set(state => ({
            libraryGames: state.libraryGames.map(g => g.id === gameId
              ? { ...g, bindings: (g.bindings || []).filter(b =>
                  (b.lesson_id !== lessonId || !lessonId) &&
                  (b.teacher_lesson_id !== teacherLessonId || !teacherLessonId)
                ) }
              : g),
            lessonGamesByLesson: {
              ...state.lessonGamesByLesson,
              [ownerId]: (state.lessonGamesByLesson[ownerId] || []).filter(g => g.id !== gameId),
            },
            libraryGamesFetchedAt: null,
          }));
          return { success: true };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      reorderLessonGames: async (lessonId, orderedIds, isTeacher = false) => {
        try {
          const path = isTeacher
            ? `${API.LESSON_GAMES}/teacher-lesson/${lessonId}/reorder`
            : `${API.LESSON_GAMES}/lesson/${lessonId}/reorder`;
          const res = await fetch(path, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ order: orderedIds }),
          });
          const data = await res.json();
          if (res.ok) {
            set(state => ({
              lessonGamesByLesson: { ...state.lessonGamesByLesson, [lessonId]: data.games || [] },
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error reordering games:', err);
          return false;
        }
      },

      // ========== Teacher-authored lessons ==========
      teacherLessons: [],
      teacherLessonsLoading: false,

      fetchTeacherLessons: async () => {
        set({ teacherLessonsLoading: true });
        try {
          const res = await fetch(`${API.TEACHER_LESSONS}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ teacherLessons: data.lessons || [], teacherLessonsLoading: false });
            return data.lessons || [];
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching teacher lessons:', err);
        }
        set({ teacherLessonsLoading: false });
        return [];
      },

      createTeacherLesson: async (payload) => {
        try {
          const res = await fetch(`${API.TEACHER_LESSONS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (res.ok) {
            set(state => ({ teacherLessons: [data.lesson, ...state.teacherLessons] }));
            return { success: true, lesson: data.lesson };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      updateTeacherLesson: async (id, updates) => {
        try {
          const res = await fetch(`${API.TEACHER_LESSONS}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updates),
          });
          const data = await res.json();
          if (res.ok) {
            set(state => ({
              teacherLessons: state.teacherLessons.map(l => l.id === id ? { ...l, ...data.lesson } : l),
            }));
            return { success: true, lesson: data.lesson };
          }
          return { success: false, error: data.error };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      deleteTeacherLesson: async (id) => {
        try {
          const res = await fetch(`${API.TEACHER_LESSONS}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set(state => ({ teacherLessons: state.teacherLessons.filter(l => l.id !== id) }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting teacher lesson:', err);
          return false;
        }
      },

      fetchRepetitionPath: async (studentId) => {
        set({ loadingRepetitionPath: true });
        const { data, error } = await api.get(`/api/lesson-games/path/${studentId}`);
        if (error) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching repetition path:', error);
          set({ loadingRepetitionPath: false });
          return null;
        }
        set({ repetitionPath: data, loadingRepetitionPath: false });
        return data;
      },

      submitGameAttempt: async (gameId, studentId, score) => {
        const { data, error } = await api.post(`/api/lesson-games/${gameId}/attempts`, { studentId, score });
        if (error) return { success: false, error };
        return { success: true, progress: data.progress };
      },

      // Presentations API methods with request deduplication and caching
      fetchPresentations: async (type = null, forceRefresh = false) => {
        const state = useEducationStore.getState();
        const CACHE_TTL = CACHE_TTL_MS;

        // Check if we have a recent fetch (within TTL) and not forcing refresh
        if (!forceRefresh && state.presentationsFetchedAt) {
          const timeSinceLastFetch = Date.now() - state.presentationsFetchedAt;
          if (timeSinceLastFetch < CACHE_TTL && state.presentations.length > 0) {
            // Return cached data if it matches the type filter
            if (!type || state.presentations.some(p => p.type === type)) {
              return;
            }
          }
        }

        // Prevent duplicate concurrent requests
        if (state.presentationsFetchPromise) {
          return state.presentationsFetchPromise;
        }

        const PAGE_SIZE = 50;
        const fetchPromise = (async () => {
          set({ loadingPresentations: true, presentationError: null });
          try {
            // Use includeFirstSlide for list view (only first slide for thumbnails) - much faster than includeSlides
            const params = new URLSearchParams({ includeFirstSlide: 'true', limit: String(PAGE_SIZE), offset: '0' });
            if (type) params.set('type', type);
            const url = `${API.PRESENTATIONS}?${params.toString()}`;
            const res = await fetch(url, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
              const fresh = (data.presentations || []).map(p => ({
                ...p,
                createdAt: p.created_at || p.createdAt,
                updatedAt: p.updated_at || p.updatedAt,
                slidesCount: p.slides_count || p.slidesCount || 0,
              }));
              // Merge instead of replace. The list endpoint is scoped
              // (e.g. teachers only see their own presentations) but callers
              // may have loaded other presentations via direct GET /:id
              // (e.g. VirtualClassroom loads the lesson's presentation even
              // if authored by someone else). Also prefer any locally-cached
              // entry that has full slide data over a light list entry, so a
              // list refresh doesn't erase already-loaded slides.
              set(state => {
                const local = state.presentations || [];
                const localById = new Map(local.map(p => [p.id, p]));
                const freshIds = new Set(fresh.map(p => p.id));
                const merged = fresh.map(newP => {
                  const existing = localById.get(newP.id);
                  const existingSlides = Array.isArray(existing?.slides) ? existing.slides.length : 0;
                  const newSlides = Array.isArray(newP.slides) ? newP.slides.length : 0;
                  return existing && existingSlides > newSlides ? existing : newP;
                });
                const preserved = local.filter(p => !freshIds.has(p.id));
                const pagination = data.pagination || {};
                return {
                  presentations: [...merged, ...preserved],
                  loadingPresentations: false,
                  presentationsFetchedAt: Date.now(),
                  presentationsFetchPromise: null,
                  presentationsPagination: {
                    limit: PAGE_SIZE,
                    offset: fresh.length,
                    total: typeof pagination.total === 'number' ? pagination.total : fresh.length,
                    hasMore: typeof pagination.hasMore === 'boolean' ? pagination.hasMore : false
                  }
                };
              });
            } else {
              set({
                presentationError: data.error,
                loadingPresentations: false,
                presentationsFetchPromise: null
              });
            }
          } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error('Error fetching presentations:', err);
            set({
              presentationError: err.message,
              loadingPresentations: false,
              presentationsFetchPromise: null
            });
          }
        })();

        set({ presentationsFetchPromise: fetchPromise });
        return fetchPromise;
      },

      loadMorePresentations: async (type = null) => {
        const state = useEducationStore.getState();
        if (state.loadingMorePresentations || state.loadingPresentations) return;
        const { limit, offset, hasMore } = state.presentationsPagination || {};
        if (!hasMore) return;

        set({ loadingMorePresentations: true, presentationError: null });
        try {
          const params = new URLSearchParams({
            includeFirstSlide: 'true',
            limit: String(limit || 50),
            offset: String(offset || 0)
          });
          if (type) params.set('type', type);
          const res = await fetch(`${API.PRESENTATIONS}?${params.toString()}`, { credentials: 'include' });
          const data = await res.json();
          if (!res.ok) {
            set({ loadingMorePresentations: false, presentationError: data.error });
            return;
          }
          const fresh = (data.presentations || []).map(p => ({
            ...p,
            createdAt: p.created_at || p.createdAt,
            updatedAt: p.updated_at || p.updatedAt,
            slidesCount: p.slides_count || p.slidesCount || 0,
          }));
          set(prev => {
            const existingIds = new Set((prev.presentations || []).map(p => p.id));
            const newOnes = fresh.filter(p => !existingIds.has(p.id));
            const pagination = data.pagination || {};
            return {
              presentations: [...(prev.presentations || []), ...newOnes],
              loadingMorePresentations: false,
              presentationsPagination: {
                limit: limit || 50,
                offset: (offset || 0) + fresh.length,
                total: typeof pagination.total === 'number' ? pagination.total : prev.presentationsPagination.total,
                hasMore: typeof pagination.hasMore === 'boolean' ? pagination.hasMore : false
              }
            };
          });
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error loading more presentations:', err);
          set({ loadingMorePresentations: false, presentationError: err.message });
        }
      },

      // Fetch a single presentation with all slides. Deliberately does not call
      // fetchPresentations() — the list endpoint filters by created_by for
      // teachers and would clobber a full presentation authored by someone
      // else. Dedupes concurrent calls per id (CameraPreview prefetch +
      // classroom on-enter fetch) via fullPresentationFetches, and skips
      // repeat fetches for ids already loaded this session via
      // fullPresentationFetchedIds.
      fetchPresentationWithSlides: async (presentationId) => {
        if (!presentationId) return null;
        const state = useEducationStore.getState();

        if (state.fullPresentationFetchedIds.includes(presentationId)) {
          return state.presentations.find(p => p.id === presentationId) || null;
        }

        if (state.fullPresentationFetches[presentationId]) {
          return state.fullPresentationFetches[presentationId];
        }

        const fetchPromise = (async () => {
          try {
            const res = await fetch(
              `${API.PRESENTATIONS}/${presentationId}?includeSlides=true`,
              { credentials: 'include' }
            );
            if (!res.ok) {
              console.error(
                `[presentation] fetch ${presentationId} failed: ${res.status} ${res.statusText}`
              );
              return null;
            }
            const data = await res.json();
            const full = {
              ...data.presentation,
              createdAt: data.presentation.created_at || data.presentation.createdAt,
              updatedAt: data.presentation.updated_at || data.presentation.updatedAt,
              slidesCount: data.presentation.slides_count || data.presentation.slidesCount || 0,
            };
            set((s) => {
              const exists = s.presentations.some(p => p.id === presentationId);
              return {
                presentations: exists
                  ? s.presentations.map(p => p.id === presentationId ? full : p)
                  : [...s.presentations, full],
                fullPresentationFetchedIds: s.fullPresentationFetchedIds.includes(presentationId)
                  ? s.fullPresentationFetchedIds
                  : [...s.fullPresentationFetchedIds, presentationId],
              };
            });
            return full;
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Error fetching full presentation:', err);
            }
            return null;
          } finally {
            set((s) => {
              const next = { ...s.fullPresentationFetches };
              delete next[presentationId];
              return { fullPresentationFetches: next };
            });
          }
        })();

        set((s) => ({
          fullPresentationFetches: {
            ...s.fullPresentationFetches,
            [presentationId]: fetchPromise,
          },
        }));

        return fetchPromise;
      },

      createPresentation: async (presentationData, type = 'program') => {
        set({ loadingPresentations: true, presentationError: null });
        try {
          const res = await fetch(`${API.PRESENTATIONS}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ...presentationData, type }),
          });
          const data = await res.json();
          if (res.ok) {
            const pres = {
              ...data.presentation,
              createdAt: data.presentation.created_at || data.presentation.createdAt,
              updatedAt: data.presentation.updated_at || data.presentation.updatedAt,
              slidesCount: data.presentation.slides_count || data.presentation.slidesCount || 0,
            };
            set((state) => ({
              presentations: [...state.presentations, pres],
              loadingPresentations: false,
            }));
            return pres;
          } else {
            set({ presentationError: data.error, loadingPresentations: false });
            return null;
          }
        } catch (err) {
          set({ presentationError: err.message, loadingPresentations: false });
          return null;
        }
      },

      updatePresentation: async (id, presentationData, type = null) => {
        set({ presentationError: null });
        try {
          const payload = type ? { ...presentationData, type } : presentationData;
          const res = await fetch(`${API.PRESENTATIONS}/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (res.ok) {
            const updated = {
              ...data.presentation,
              createdAt: data.presentation.created_at || data.presentation.createdAt,
              updatedAt: data.presentation.updated_at || data.presentation.updatedAt,
              slidesCount: data.presentation.slides_count || data.presentation.slidesCount || 0,
            };
            set((state) => ({
              presentations: state.presentations.map(p => p.id === id ? updated : p),
            }));
            return updated;
          } else if (res.status === 404) {
            // Presentation doesn't exist in DB yet - create it
            const createPayload = type ? { id, ...presentationData, type } : { id, ...presentationData };
            const createRes = await fetch(`${API.PRESENTATIONS}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(createPayload),
            });
            const createData = await createRes.json();
            if (createRes.ok) {
              const created = {
                ...createData.presentation,
                createdAt: createData.presentation.created_at || createData.presentation.createdAt,
                updatedAt: createData.presentation.updated_at || createData.presentation.updatedAt,
                slidesCount: createData.presentation.slides_count || createData.presentation.slidesCount || 0,
              };
              set((state) => ({
                presentations: [...state.presentations, created],
              }));
              return created;
            } else {
              set({ presentationError: createData.error });
              return null;
            }
          } else {
            set({ presentationError: data.error });
            return null;
          }
        } catch (err) {
          set({ presentationError: err.message });
          return null;
        }
      },

      deletePresentation: async (id) => {
        try {
          const res = await fetch(`${API.PRESENTATIONS}/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              presentations: state.presentations.filter(p => p.id !== id),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting presentation:', err);
          return false;
        }
      },

      // Fetch teachers from API
      fetchTeachers: async (includeArchived = false) => {
        set({ loadingTeachers: true, teacherError: null });
        try {
          const res = await fetch(`${API.USERS}/teachers?includeArchived=${includeArchived}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ teachers: data.teachers || [], loadingTeachers: false });
          } else {
            set({ teacherError: data.error, loadingTeachers: false });
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching teachers:', err);
          set({ loadingTeachers: false });
        }
      },

      // Create teacher invitation
      createTeacherInvitation: async (invitationData) => {
        try {
          const { data, error } = await api.post('/api/teacher-invitations', invitationData);

          if (error) {
            return { success: false, error };
          }

          return { success: true, invitation: data.invitation, registrationCode: data.registrationCode };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('createTeacherInvitation error:', err);
          return { success: false, error: err.message };
        }
      },

      // Archive teacher
      archiveTeacher: async (teacherId, archivedBy) => {
        try {
          const res = await fetch(`${API.USERS}/${teacherId}/archive`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ archivedBy }),
          });
          if (res.ok) {
            set((state) => ({
              teachers: state.teachers.map(t =>
                t.id === teacherId ? { ...t, archived: true } : t
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error archiving teacher:', err);
          return false;
        }
      },

      // Update teacher career level
      updateTeacherLevel: async (teacherId, level) => {
        try {
          const res = await fetch(`${API.USERS}/${teacherId}/teacher-level`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ level }),
          });
          if (res.ok) {
            set((state) => ({
              teachers: state.teachers.map(t =>
                t.id === teacherId ? { ...t, teacher_level: level } : t
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error updating teacher level:', err);
          return false;
        }
      },

      // Mark teacher as "author of methodology" and set their individual-lesson price
      updateAuthorTeacher: async (teacherId, isAuthor, price) => {
        try {
          const res = await fetch(`${API.USERS}/${teacherId}/author-teacher`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              is_method_author: isAuthor,
              author_individual_price: isAuthor ? price : null,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            set((state) => ({
              teachers: state.teachers.map(t =>
                t.id === teacherId
                  ? { ...t, is_method_author: data.user.is_method_author, author_individual_price: data.user.author_individual_price }
                  : t
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error updating author-teacher flag:', err);
          return false;
        }
      },

      // Unarchive teacher
      unarchiveTeacher: async (teacherId) => {
        try {
          const res = await fetch(`${API.USERS}/${teacherId}/unarchive`, {
            method: 'PUT',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              teachers: state.teachers.map(t =>
                t.id === teacherId ? { ...t, archived: false } : t
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error unarchiving teacher:', err);
          return false;
        }
      },

      // Fetch students from API
      fetchStudents: async (includeArchived = false) => {
        set({ loadingStudents: true, studentError: null });
        try {
          const res = await fetch(`${API.USERS}/students?includeArchived=${includeArchived}&includeChildren=true`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ students: data.students || [], loadingStudents: false });
          } else {
            set({ studentError: data.error, loadingStudents: false });
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching students:', err);
          set({ loadingStudents: false });
        }
      },

      // Archive student
      archiveStudent: async (studentId, archivedBy) => {
        try {
          const res = await fetch(`${API.USERS}/${studentId}/archive`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ archivedBy }),
          });
          if (res.ok) {
            set((state) => ({
              students: state.students.map(s =>
                s.id === studentId ? { ...s, archived: true, status: 'archived' } : s
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error archiving student:', err);
          return false;
        }
      },

      // Permanently delete a student (used for unverified registrations)
      deleteStudent: async (studentId) => {
        try {
          const res = await fetch(`${API.USERS}/${studentId}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              students: state.students.filter(s => s.id !== studentId),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error deleting student:', err);
          return false;
        }
      },

      // Unarchive student
      unarchiveStudent: async (studentId) => {
        try {
          const res = await fetch(`${API.USERS}/${studentId}/unarchive`, {
            method: 'PUT',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              students: state.students.map(s =>
                s.id === studentId ? { ...s, archived: false, status: 'active' } : s
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error unarchiving student:', err);
          return false;
        }
      },

      // Fetch admins from API
      fetchAdmins: async (includeArchived = false) => {
        set({ loadingAdmins: true, adminError: null });
        try {
          const res = await fetch(`${API.USERS}/admins?includeArchived=${includeArchived}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            set({ admins: data.admins || [], loadingAdmins: false });
          } else {
            set({ adminError: data.error, loadingAdmins: false });
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching admins:', err);
          set({ loadingAdmins: false });
        }
      },

      // Fetch admin permissions list
      fetchAdminPermissions: async () => {
        try {
          const { data, error } = await api.get('/api/admin-invitations/permissions');
          if (error) {
            return { success: false, error };
          }
          return { success: true, permissions: data.permissions };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching admin permissions:', err);
          return { success: false, error: err.message };
        }
      },

      // Create admin invitation
      createAdminInvitation: async (invitationData) => {
        try {
          const { data, error } = await api.post('/api/admin-invitations', invitationData);

          if (error) {
            return { success: false, error };
          }

          return { success: true, invitation: data.invitation, registrationCode: data.registrationCode };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('createAdminInvitation error:', err);
          return { success: false, error: err.message };
        }
      },

      // Archive admin
      archiveAdmin: async (adminId, archivedBy) => {
        try {
          const res = await fetch(`${API.USERS}/${adminId}/archive`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ archivedBy }),
          });
          if (res.ok) {
            set((state) => ({
              admins: state.admins.map(a =>
                a.id === adminId ? { ...a, archived: true } : a
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error archiving admin:', err);
          return false;
        }
      },

      // Unarchive admin
      unarchiveAdmin: async (adminId) => {
        try {
          const res = await fetch(`${API.USERS}/${adminId}/unarchive`, {
            method: 'PUT',
            credentials: 'include',
          });
          if (res.ok) {
            set((state) => ({
              admins: state.admins.map(a =>
                a.id === adminId ? { ...a, archived: false } : a
              ),
            }));
            return true;
          }
          return false;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error unarchiving admin:', err);
          return false;
        }
      },

      // Fetch teacher invitations
      fetchTeacherInvitations: async () => {
        set({ loadingTeacherInvitations: true });
        try {
          const res = await fetch(API.TEACHER_INVITATIONS, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            set({ teacherInvitations: data.invitations || [], loadingTeacherInvitations: false });
            return data.invitations || [];
          }
          // Don't trigger auth error - just return empty array
          set({ loadingTeacherInvitations: false });
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching teacher invitations:', err);
          set({ loadingTeacherInvitations: false });
          return [];
        }
      },

      // Fetch admin invitations
      fetchAdminInvitations: async () => {
        set({ loadingAdminInvitations: true });
        try {
          const res = await fetch(API.ADMIN_INVITATIONS, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            set({ adminInvitations: data.invitations || [], loadingAdminInvitations: false });
            return data.invitations || [];
          }
          // Don't trigger auth error - just return empty array
          set({ loadingAdminInvitations: false });
          return [];
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching admin invitations:', err);
          set({ loadingAdminInvitations: false });
          return [];
        }
      },

      // Cancel teacher invitation
      cancelTeacherInvitation: async (invitationId) => {
        try {
          const res = await fetch(`${API.TEACHER_INVITATIONS}/${invitationId}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          if (res.ok) {
            set((state) => ({
              teacherInvitations: state.teacherInvitations.filter(inv => inv.id !== invitationId)
            }));
            return { success: true };
          }
          return { success: false, error: 'Не вдалося скасувати запрошення' };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error canceling teacher invitation:', err);
          return { success: false, error: err.message };
        }
      },

      // Cancel admin invitation
      cancelAdminInvitation: async (invitationId) => {
        try {
          const res = await fetch(`${API.ADMIN_INVITATIONS}/${invitationId}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          if (res.ok) {
            set((state) => ({
              adminInvitations: state.adminInvitations.filter(inv => inv.id !== invitationId)
            }));
            return { success: true };
          }
          return { success: false, error: 'Не вдалося скасувати запрошення' };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error canceling admin invitation:', err);
          return { success: false, error: err.message };
        }
      },

      // Resend teacher invitation
      resendTeacherInvitation: async (invitationId) => {
        try {
          const res = await fetch(`${API.TEACHER_INVITATIONS}/${invitationId}/resend`, {
            method: 'POST',
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            set((state) => ({
              teacherInvitations: state.teacherInvitations.map(inv =>
                inv.id === invitationId ? { ...inv, expiresAt: data.expiresAt, expires_at: data.expiresAt } : inv
              )
            }));
            return { success: true, message: data.message };
          }
          return { success: false, error: 'Не вдалося надіслати повторно' };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error resending teacher invitation:', err);
          return { success: false, error: err.message };
        }
      },

      // Resend admin invitation
      resendAdminInvitation: async (invitationId) => {
        try {
          const res = await fetch(`${API.ADMIN_INVITATIONS}/${invitationId}/resend`, {
            method: 'POST',
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            set((state) => ({
              adminInvitations: state.adminInvitations.map(inv =>
                inv.id === invitationId ? { ...inv, expiresAt: data.expiresAt, expires_at: data.expiresAt } : inv
              )
            }));
            return { success: true, message: data.message };
          }
          return { success: false, error: 'Не вдалося надіслати повторно' };
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error resending admin invitation:', err);
          return { success: false, error: err.message };
        }
      },

      // Fetch all visible users from API (for chat search)
      fetchAllUsers: async () => {
        set({ loadingUsers: true, usersError: null });
        try {
          const res = await fetch(`${API.USERS}`, { credentials: 'include' });
          const data = await res.json();
          if (res.ok) {
            // Map backend fields to frontend format
            const users = (data.users || []).map(u => ({
              id: u.id,
              name: u.name,
              nickname: u.nickname || '',
              email: u.email || '',
              age: u.age,
              birthDate: u.date_of_birth,
              role: u.role === 'teacher' ? 'Вчитель' : u.role === 'student' ? 'Учень' : u.role === 'admin' ? 'Адміністратор' : u.role,
              avatar: u.avatar || u.name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '??',
              avatar_url: u.avatar_url,
              color: u.color || 'bg-gray-500',
              profile_visible: u.profile_visible,
              parent_id: u.parent_id,
              skillLevels: u.skill_levels,
              levelTestCompleted: u.level_test_completed,
              registered_at: u.created_at,
            }));
            set({ allUsers: users, loadingUsers: false });
          } else {
            set({ usersError: data.error, loadingUsers: false });
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching users:', err);
          set({ loadingUsers: false });
        }
      },
    }),
    {
      name: 'lingvish_education',
      version: 4,
      partialize: (state) => ({
        studentNotes: state.studentNotes,
      }),
      migrate: (persistedState, version) => {
        if (version < 4) {
          // Clear all cached data on version upgrade - force fresh fetch from API
          return {
            studentNotes: persistedState.studentNotes || [],
          };
        }
        return persistedState;
      },
    }
  )
);

export default useEducationStore;
